import PropTypes, { func } from 'prop-types';
import React from 'react';
import {
  Add20Regular,
  Delete24Regular,
} from '@fluentui/react-icons';
import {
  Button,
  Input,
  mergeClasses,
  Skeleton,
  SkeletonItem,
  Text,
  useId,
} from '@fluentui/react-components';
import { useTaglinesStyles } from './styles';

const TaglinesLoadingSkeleton = () => {
  const classes = useTaglinesStyles();
  return (
    <Skeleton className={classes.loading} aria-label="Loading Taglines">
      <div className={classes.loadingItem}>
        <SkeletonItem size={32} />
        <SkeletonItem shape="square" size={32} />
      </div>
      <div className={classes.loadingItem}>
        <SkeletonItem size={32} />
        <SkeletonItem shape="square" size={32} />
      </div>
    </Skeleton>
  );
};

const TaglineItem = ({
  i18n,
  tagline,
  onChange,
  onDelete,
  isDeleteDisabled,
  maxAllowedCharacters,
}) => {
  const classes = useTaglinesStyles();
  const inputId = useId('tagline-input');

  return (
    <div className={mergeClasses('tagline-item', classes.taglineItem)}>
      <div
        className={mergeClasses('tagline-item-input', classes.taglineItemInput)}
      >
        <Input
          autoFocus
          id={inputId}
          value={tagline}
          onChange={ev => onChange(ev.target.value)}
        />
        <Text size={300} align="end" className="input-validation-text">
          {i18n.getString(_TL_('{{a}} characters remaining'), {
            a: maxAllowedCharacters - tagline.length,
          })}
        </Text>
      </div>
      <Button
        appearance="subtle"
        icon={<Delete24Regular />}
        disabled={isDeleteDisabled}
        onClick={onDelete}
      />
    </div>
  );
};

TaglineItem.propTypes = {
  i18n: PropTypes.shape({ getString: func }).isRequired,
  tagline: PropTypes.string,
  onChange: PropTypes.func,
  onDelete: PropTypes.func,
  isDeleteDisabled: PropTypes.bool,
  maxAllowedCharacters: PropTypes.number,
};

TaglineItem.defaultProps = {
  tagline: '',
  onChange: () => {},
  onDelete: () => {},
  isDeleteDisabled: false,
  maxAllowedCharacters: 30,
};

export const Taglines = ({
  i18n,
  loading,
  taglines,
  onChangeTaglines,
  maxAllowedTaglines,
  maxAllowedCharactersPerTagline,
}) => {
  const classes = useTaglinesStyles();

  const isAddTaglineButtonDisabled =
    taglines &&
    (taglines.some(tagline => !tagline.length) ||
      (maxAllowedTaglines && taglines.length >= maxAllowedTaglines));

  const handleAddTagline = () => {
    onChangeTaglines([...taglines, '']);
  };

  const handleDeleteTagline = (index) => {
    const newTaglines = [...taglines];
    newTaglines.splice(index, 1);
    onChangeTaglines(newTaglines);
  };

  const handleChangeTagline = (value, index) => {
    if (maxAllowedTaglines && taglines.length > maxAllowedTaglines) {
      return;
    }

    const newTaglines = [...taglines];
    newTaglines[index] = value.substring(0, maxAllowedCharactersPerTagline);
    onChangeTaglines(newTaglines);
  };

  return (
    <div className={mergeClasses('taglines-body', classes.body)}>
      {loading ? (
        <TaglinesLoadingSkeleton />
      ) : (
        taglines.map((tagline, index) => (
          <TaglineItem
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            i18n={i18n}
            tagline={tagline}
            isDeleteDisabled={taglines.length === 1}
            onDelete={() => handleDeleteTagline(index)}
            maxAllowedCharacters={maxAllowedCharactersPerTagline}
            onChange={value => handleChangeTagline(value, index)}
          />
        ))
      )}
      <Button
        icon={<Add20Regular />}
        onClick={handleAddTagline}
        className="add-tagline-button"
        disabled={loading || isAddTaglineButtonDisabled}
      >
        {i18n.getString(_TL_('Add Tagline'))}
      </Button>
    </div>
  );
};

Taglines.propTypes = {
  loading: PropTypes.bool,
  i18n: PropTypes.shape({ getString: func }).isRequired,
  taglines: PropTypes.arrayOf(PropTypes.string),
  maxAllowedTaglines: PropTypes.number,
  maxAllowedCharactersPerTagline: PropTypes.number,
  onChangeTaglines: PropTypes.func,
};

Taglines.defaultProps = {
  taglines: [''],
  loading: false,
  maxAllowedTaglines: undefined,
  maxAllowedCharactersPerTagline: 30,
  onChangeTaglines: () => {},
};
